import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api } from '../api';
import GlassCard from './ui/GlassCard';
import Badge from './ui/Badge';
import Btn from './ui/Btn';
import Field from './ui/Field';
import ConfirmDialog from './ui/ConfirmDialog';
import EmptyState from './ui/EmptyState';
import SearchBar from './ui/SearchBar';
import Header from './ui/Header';
import PillBtn from './ui/PillBtn';
import ArchiveSection from './ArchiveSection';
import KitLink, { KitLinkFields } from './ui/KitLink';
import InfoTable from './ui/InfoTable';
import Icon from './ui/Icon';
import { formatDate, formatDateTime, formatMoney } from '../utils/format';
import PhotoSlider from './ui/PhotoSlider';

interface Client {
  id: number;
  name: string;
}

interface Repair extends KitLinkFields {
  id: number;
  repair_number?: string;
  client_id?: number;
  client_name?: string;
  city?: string;
  device: string;
  defect?: string;
  work_done?: string;
  cost: number;
  paid: boolean;
  created_at: string;
  image_paths?: string;
}

interface RepairForm {
  client_id: number | string;
  client_name_search: string;
  city: string;
  device: string;
  defect: string;
  work_done: string;
  cost: number | string;
  // Только при создании: заполненное поле рождает запись в комплектации.
  kit_content: string;
}

interface RepairsSectionProps {
  filterClientId?: number | null;
  onClearFilter?: () => void;
  openRepairId?: number | null;
  onRepairOpened?: () => void;
  onOpenKit?: (kitId: number, archived: boolean) => void;
  // Переход к архивному ремонту из комплектации.
  openArchiveRepairId?: number | null;
  onArchiveRepairOpened?: () => void;
}

type View = 'list' | 'detail' | 'form';
type Tab = 'active' | 'archive';

const RepairsSection: React.FC<RepairsSectionProps> = ({ filterClientId, onClearFilter, openRepairId, onRepairOpened, onOpenKit, openArchiveRepairId, onArchiveRepairOpened }) => {
  const [repairs, setRepairs] = useState<Repair[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('list');
  const [tab, setTab] = useState<Tab>('active');
  const [selected, setSelected] = useState<Repair | null>(null);
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [form, setForm] = useState<RepairForm>({
    client_id: '', client_name_search: '', city: '', device: '', defect: '', work_done: '', cost: '', kit_content: '',
  });
  const [errors, setErrors] = useState<Partial<RepairForm>>({});
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [showClientDrop, setShowClientDrop] = useState(false);
  const clientRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (clientRef.current && !clientRef.current.contains(e.target as Node))
        setShowClientDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [repRes, clientRes] = await Promise.all([
        filterClientId ? api.getClientRepairs(filterClientId) : api.getRepairs(),
        api.getClients(),
      ]);
      setRepairs(repRes.data);
      setClients(clientRes.data);
    } catch (err) {
      console.error('Error loading repairs', err);
    } finally {
      setLoading(false);
    }
  }, [filterClientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Переход из комплектации: открываем карточку ремонта, как только список загружен.
  useEffect(() => {
    if (!openRepairId || loading) return;
    const repair = repairs.find(r => r.id === openRepairId);
    if (repair) {
      setTab('active');
      setSelected(repair);
      setView('detail');
    }
    onRepairOpened?.();
  }, [openRepairId, loading, repairs, onRepairOpened]);

  const displayed = useMemo(() => {
    let r = repairs;
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(x =>
        x.device.toLowerCase().includes(q) ||
        (x.repair_number || '').includes(q) ||
        (x.client_name || '').toLowerCase().includes(q)
      );
    }
    return [...r].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [repairs, search]);

  const unpaid = displayed.filter(r => !r.paid).reduce((s, r) => s + Number(r.cost), 0);

  const openAdd = () => {
    const preClient = filterClientId ? clients.find(c => c.id === filterClientId) : null;
    setForm({ client_id: filterClientId || '', client_name_search: preClient?.name || '', city: '', device: '', defect: '', work_done: '', cost: '', kit_content: '' });
    setErrors({});
    setSelected(null);
    setView('form');
  };

  const openEdit = (repair: Repair) => {
    const clientName = repair.client_name || clients.find(c => c.id === repair.client_id)?.name || '';
    setForm({
      client_id: repair.client_id || '',
      client_name_search: clientName,
      city: repair.city || '',
      device: repair.device,
      defect: repair.defect || '',
      work_done: repair.work_done || '',
      cost: repair.cost,
      kit_content: '',
    });
    const imgs = repair.image_paths ? (() => { try { return JSON.parse(repair.image_paths); } catch { return []; } })() : [];
    setImages(imgs);
    setErrors({});
    setSelected(repair);
    setView('form');
  };

  const save = async () => {
    const err: Partial<RepairForm> = {};
    if (!form.device.trim()) err.device = 'Поле обязательно';
    if (Object.keys(err).length) { setErrors(err); return; }

    setSaving(true);
    try {
      const { client_name_search: _cns, ...formData } = form;
      const payload = {
        ...formData,
        client_id: form.client_id || null,
        cost: Number(form.cost) || 0,
      };

      if (selected) {
        await api.updateRepair(selected.id, payload);
      } else {
        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => {
          if (v !== null && v !== undefined) fd.append(k, String(v));
        });
        await api.createRepair(fd);
      }
      await loadData();
      setView('list');
    } catch (err) {
      console.error('Save error', err);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!selected) return;
    try {
      await api.deleteRepair(selected.id, undefined, true);
      setRepairs(rs => rs.filter(r => r.id !== selected.id));
      setView('list');
      setConfirm(false);
    } catch (err) {
      console.error('Delete error', err);
    }
  };

  const markPaidAndArchive = async (repair: Repair) => {
    try {
      await api.updateRepair(repair.id, {
        client_id: repair.client_id || null,
        city: repair.city || '',
        device: repair.device,
        defect: repair.defect || '',
        work_done: repair.work_done || '',
        cost: repair.cost,
        paid: true,
      });
      await api.deleteRepair(repair.id, 'Оплачено');
      setRepairs(rs => rs.filter(r => r.id !== repair.id));
      if (selected?.id === repair.id) setView('list');
    } catch (err) {
      console.error('Mark paid error', err);
    }
  };

  const getClientName = (id: number | null | undefined) => {
    if (!id) return null;
    return clients.find(c => c.id === id)?.name || null;
  };

  // Ссылка на архивный ремонт: сначала переключаемся на архив, дальше он откроет запись сам.
  useEffect(() => {
    if (openArchiveRepairId) setTab('archive');
  }, [openArchiveRepairId]);

  const tabs = (
    <>
      <PillBtn active={tab === 'active'} onClick={() => setTab('active')}>Активные</PillBtn>
      <PillBtn active={tab === 'archive'} onClick={() => setTab('archive')}>Архив</PillBtn>
    </>
  );

  if (tab === 'archive') {
    return (
      <ArchiveSection
        title="Ремонты"
        toolbar={tabs}
        onOpenKit={onOpenKit}
        openId={openArchiveRepairId}
        onOpened={onArchiveRepairOpened}
      />
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Header title="Ремонты" />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text2)' }}>
          Загрузка...
        </div>
      </div>
    );
  }

  if (view === 'form') return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header title={selected ? 'Редактировать' : 'Новый ремонт'} onBack={() => setView(selected ? 'detail' : 'list')} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 100px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 520 }}>
          <Field label="Клиент">
            <div ref={clientRef} style={{ position: 'relative' }}>
              <input
                value={form.client_name_search}
                onChange={e => {
                  setForm(f => ({ ...f, client_name_search: e.target.value, client_id: '' }));
                  setShowClientDrop(true);
                }}
                onFocus={() => setShowClientDrop(true)}
                placeholder="Поиск клиента..."
              />
              {showClientDrop && (() => {
                const filtered = form.client_name_search.trim()
                  ? clients.filter(c => c.name.toLowerCase().includes(form.client_name_search.toLowerCase()))
                  : clients;
                return filtered.length > 0 ? (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                    zIndex: 200,
                    background: 'var(--bg-canvas)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--r-sm)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    maxHeight: 200, overflowY: 'auto',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                  }}>
                    {filtered.map(c => (
                      <div
                        key={c.id}
                        onMouseDown={() => {
                          setForm(f => ({ ...f, client_id: c.id, client_name_search: c.name }));
                          setShowClientDrop(false);
                        }}
                        style={{
                          padding: '10px 14px',
                          fontSize: 14,
                          color: 'var(--text0)',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--glass-border)',
                          fontFamily: 'var(--sans)',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--glass-bg)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        {c.name}
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}
            </div>
          </Field>
          <Field label="Город">
            <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Город" />
          </Field>
          <Field label="Устройство" required error={String(errors.device || '')}>
            <input value={form.device} onChange={e => setForm(f => ({ ...f, device: e.target.value }))} placeholder="Название устройства" />
          </Field>
          <Field label="Неисправность">
            <textarea value={form.defect} onChange={e => setForm(f => ({ ...f, defect: e.target.value }))} placeholder="Описание..." />
          </Field>
          <Field label="Выполненные работы">
            <textarea value={form.work_done} onChange={e => setForm(f => ({ ...f, work_done: e.target.value }))} placeholder="Что было сделано..." />
          </Field>
          {!selected && (
            <Field label="Что было в комплекте">
              <textarea
                value={form.kit_content}
                onChange={e => setForm(f => ({ ...f, kit_content: e.target.value }))}
                placeholder="Блок питания, шлейф, 4 винта... (уйдёт в комплектацию)"
                rows={3}
              />
            </Field>
          )}
          <Field label="Стоимость, ₽">
            <input type="number" min="0" value={form.cost} onChange={e => setForm(f => ({ ...f, cost: e.target.value }))} placeholder="0" />
          </Field>
        </div>
      </div>
      <div style={{ padding: '16px 20px', borderTop: '1px solid var(--glass-border)', display: 'flex', gap: 10 }}>
        <Btn variant="primary" onClick={save} icon="check" disabled={saving} style={{ flex: 1, justifyContent: 'center' }}>
          {saving ? 'Сохранение...' : 'Сохранить'}
        </Btn>
        <Btn variant="ghost" onClick={() => setView(selected ? 'detail' : 'list')}>Отмена</Btn>
      </div>
    </div>
  );

  if (view === 'detail' && selected) {
    const clientName = selected.client_name || getClientName(selected.client_id);
    const detailImages: string[] = selected.image_paths ? (() => { try { return JSON.parse(selected.image_paths); } catch { return []; } })() : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {confirm && (
          <ConfirmDialog
            message={`Удалить ремонт ${selected.repair_number || '#' + selected.id}?`}
            onConfirm={del}
            onCancel={() => setConfirm(false)}
          />
        )}
        <Header title={selected.repair_number || `#${selected.id}`} onBack={() => setView('list')} />
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text0)' }}>{selected.device}</h2>
            <Badge variant={selected.paid ? 'paid' : 'unpaid'}>{selected.paid ? '✓ Оплачено' : '✕ Долг'}</Badge>
          </div>
          {!selected.paid && (
            <button
              onClick={() => markPaidAndArchive(selected)}
              style={{
                width: '100%', padding: '14px',
                borderRadius: 'var(--r-md)',
                border: '1px solid var(--teal-mid)',
                background: 'var(--teal-dim)',
                cursor: 'pointer',
                fontFamily: 'var(--sans)', fontWeight: 700, fontSize: 15,
                color: 'var(--teal)',
                backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                marginBottom: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                letterSpacing: '0.01em',
              }}
            >
              ✓ Оплачено — перенести в архив
            </button>
          )}
          <InfoTable rows={[
            ['Номер', selected.repair_number ? (
              <span style={{ fontFamily: 'var(--mono)', fontWeight: 600 }}>{selected.repair_number}</span>
            ) : null],
            ['Клиент', clientName || '—'],
            ['Город', selected.city || '—'],
            ['Дата', formatDateTime(selected.created_at)],
            ['Комплектация', <KitLink repair={selected} onOpenKit={onOpenKit} />],
            ['Стоимость', (
              <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 17, color: selected.paid ? 'var(--teal)' : 'var(--red)' }}>
                {formatMoney(selected.cost)}
              </span>
            )],
          ]} />
          {selected.defect && (
            <div style={{ marginTop: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                Неисправность
              </div>
              <div style={{ color: 'var(--text0)', fontSize: 14, lineHeight: 1.65, background: 'var(--glass-bg)', padding: '12px 16px', borderRadius: 'var(--r-sm)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}>
                {selected.defect}
              </div>
            </div>
          )}
          {selected.work_done && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                Выполненные работы
              </div>
              <div style={{ color: 'var(--text0)', fontSize: 14, lineHeight: 1.65, background: 'var(--glass-bg)', padding: '12px 16px', borderRadius: 'var(--r-sm)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}>
                {selected.work_done}
              </div>
            </div>
          )}
          {detailImages.length > 0 && <PhotoSlider images={detailImages} />}
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="ghost" icon="edit" onClick={() => openEdit(selected)} style={{ flex: 1, justifyContent: 'center' }}>Изменить</Btn>
            <Btn variant="danger" icon="trash" onClick={() => setConfirm(true)} style={{ flex: 1, justifyContent: 'center' }}>Удалить</Btn>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header
        title={filterClientId ? 'Ремонты клиента' : 'Ремонты'}
        onBack={filterClientId ? onClearFilter : undefined}
        action={!filterClientId ? (
          <Btn variant="primary" icon="plus" onClick={openAdd} style={{ padding: '7px 14px' }}>
            Добавить
          </Btn>
        ) : undefined}
      />
      {unpaid > 0 && (
        <div style={{
          margin: '12px 16px 0',
          background: 'var(--red-dim)',
          border: '1px solid var(--red-mid)',
          borderRadius: 'var(--r-md)',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}>
          <Icon name="alert" size={18} color="var(--red)" />
          <div>
            <div style={{ fontSize: 11, color: 'var(--red)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Общий долг
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 18, color: 'var(--red)' }}>
              {formatMoney(unpaid)}
            </div>
          </div>
        </div>
      )}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--glass-border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Устройство, клиент, номер..." />
        {!filterClientId && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>{tabs}</div>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {displayed.length === 0 ? (
          <EmptyState icon="repairs" title="Ремонтов нет" sub="Добавьте первый ремонт" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {displayed.map(r => (
              <GlassCard key={r.id} onClick={() => { setSelected(r); setView('detail'); }} style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      {r.repair_number && (
                        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text2)', fontWeight: 600 }}>
                          {r.repair_number}
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                        {formatDate(r.created_at)}
                      </span>
                    </div>
                    <div style={{ fontWeight: 600, marginBottom: 3, color: 'var(--text0)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.device}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: 'var(--text2)', fontSize: 13 }}>
                        {r.client_name || getClientName(r.client_id) || 'Без клиента'}
                      </span>
                      <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 13, color: r.paid ? 'var(--teal)' : 'var(--red)' }}>
                        {formatMoney(r.cost)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); markPaidAndArchive(r); }}
                    style={{
                      flexShrink: 0,
                      padding: '7px 12px',
                      borderRadius: 'var(--r-sm)',
                      border: '1px solid var(--red-mid)',
                      background: 'var(--red-dim)',
                      cursor: 'pointer',
                      fontFamily: 'var(--sans)', fontWeight: 600, fontSize: 12,
                      color: 'var(--red)',
                      backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Не оплачено
                  </button>
                </div>
              </GlassCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RepairsSection;
